import React, { useContext, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useHistory, Link } from 'react-router-dom';
import MaskedInput from 'react-text-mask';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import DatePickerField from '../../form/DatePickerField';
import { isRequiredField } from '../../form/RequiredAsterisk';
import { AccountContext } from '../../Accounts';
import { DataTypeContext } from '../../DataTypes';
import { API, FIND_CLIENT_STATUS } from '../../Constants';
import LabelField from '../../form/LabelField';
import ScrollToFirstError from '../../route/ScrollToFirstError';
import { formatDate, getPhoneNumberWithCode, getPhoneNumber } from '../../Utils';
import { useCustomTranslator } from '../../utils/customTranslator';

const CreateAccountFormStep1 = () => {
    const { clientId, setClientId, setLink2FeedClientId, setFirstName, setLastName, setUsername, setEmail, setPhone, setDob, setAge, userExistsByEmailPhone } = useContext(AccountContext);
    const { dataTypes } = useContext(DataTypeContext);
    const history = useHistory();
    const { t } = useTranslation();
    const customT = useCustomTranslator();

    const { questionPhrasing, idIsRequired, networkId, useIdWorkflow } = dataTypes;
    const [isLoading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const enabledFields = dataTypes.enabledFields || [];

    const schemaFields = {
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        email: Yup.string()
            .email('Email must be valid')
            .test('', 'Email or Phone is required', function (value) {
                const { phone } = this.parent;
                const phoneHasDigits = !!phone && phone.replace('+1', '').replace(/\D+/g, '').length > 0;
                if (!phoneHasDigits) return !!value && value.trim().length > 0;
                return true;
            }),
        confirmEmail: Yup.string().test('', 'Confirm Email must match Email', function (value) {
            const { email } = this.parent;
            if (!email) return true;
            return value?.toLowerCase() === email.toLowerCase();
        }),
        phone: Yup.string()
            .test('', 'Email or Phone is required', function (value) {
                const { email } = this.parent;
                const hasEmail = !!email && email.trim().length > 0;
                if (!hasEmail) {
                    const phoneDigits = (value || '').replace('+1', '').replace(/\D+/g, '');
                    return phoneDigits.length > 0;
                }
                return true;
            })
            .test('', 'Phone must be of 10 digits', function (value) {
                if (value !== undefined) {
                    const onlyNumbers = value.replace('+1', '').replace(/\D+/g, '');
                    if (onlyNumbers.length > 0) return onlyNumbers.length === 10;
                }
                return true;
            }),
        confirmPhone: Yup.string().test('', 'Confirm Phone must match Phone', function (value) {
            const { phone } = this.parent;
            if (!phone) return true;
            const onlyNumbers = phone.replace('+1', '').replace(/\D+/g, '');
            if (onlyNumbers.length === 0) return true;
            return value === phone;
        }),
    };

    if (enabledFields.includes('dateOfBirth')) {
        schemaFields.dob = Yup.date().max(new Date()).required('Date of Birth is required');
    }

    if (enabledFields.includes('age')) {
        schemaFields.age = Yup.number().positive('Age must be a positive number').integer('Age must be a whole number').required('Age is required');
    }

    const schema = Yup.object(schemaFields);

    const onSubmit = async (values) => {
        if (!isLoading) {
            setLoading(true);

            try {
                const body = {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    dateOfBirth: formatDate(values.dob),
                    networkId: networkId,
                };
                if (useIdWorkflow && clientId) {
                    body.clientId = clientId;
                }

                const url = new URL(API.CNCT_ADMIN_FIND_CLIENT_BY_DETAILS_URL);
                url.search = new URLSearchParams(body).toString();
                const userName = values.email ? values.email : getPhoneNumberWithCode(values.phone);

                setFirstName(values.firstName);
                setLastName(values.lastName);
                setUsername(userName);
                setEmail(values.email);
                setPhone(getPhoneNumber(values.phone));

                if (enabledFields.includes('age')) {
                    const age = values.age;
                    setAge(age);
                    values.dob = moment().subtract(age, 'years').toDate();
                }

                setDob(formatDate(values.dob));

                const cognitoUserResult = await userExistsByEmailPhone(values.email, values.phone);
                const checkUserExists = cognitoUserResult.userExists;
                if (checkUserExists) {
                    setUsername(cognitoUserResult.username);
                    history.push({ pathname: 'account-exists', state: { definiteMatch: true } });
                } else {
                    let findResponse = null;

                    if (useIdWorkflow) {
                        findResponse = await fetch(url);
                        if (!findResponse.ok && clientId) {
                            setClientId('');
                            setLink2FeedClientId('');
                        }
                    }

                    if (idIsRequired) {
                        if (!findResponse.ok) {
                            const err = new Error(findResponse.status === 404 ? customT('api.error.NOT_FOUND') : customT('api.error.UNKNOWN'));
                            err.code = findResponse.status;
                            throw err;
                        }
                        const findResponseJson = await findResponse.json();

                        if (findResponseJson.status === FIND_CLIENT_STATUS.MATCHES && !clientId) {
                            history.push({ pathname: 'account-exists', state: { definiteMatch: false } });
                            return;
                        }
                    }
                    history.push({ pathname: 'create-account-next' });
                }
            } catch (err) {
                setLoading(false);
                if (err?.code === 404) {
                    history.push({ pathname: 'create-account-next' });
                } else if (err.message) {
                    setResponse(err.message);
                }
            }
        }
    };

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={{}} validateOnChange={false} validateOnBlur={false}>
            {({ handleSubmit, handleChange, setFieldValue, values, touched, errors }) => {
                const handleEmailChange = (e) => {
                    handleChange(e);
                    setFieldValue('confirmEmail', '');
                };
                const handlePhoneChange = (e) => {
                    handleChange(e);
                    setFieldValue('confirmPhone', '');
                };
                return (
                <Form noValidate onSubmit={handleSubmit}>
                    <h4 className="text-center pb-4">{t('personal.createAccount')}</h4>
                    <p className="text-center">{t('personal.createAccountStepOne')}</p>
                    {questionPhrasing?.createAccountPage1 && (
                        <div className="card p-3 bg-info mb-4">
                            <p dangerouslySetInnerHTML={{ __html: questionPhrasing.createAccountPage1 }}></p>
                        </div>
                    )}
                    <Form.Group controlId="firstName">
                        <LabelField
                            label={questionPhrasing.firstName || t('personal.firstName')}
                            isRequired={isRequiredField(schema, 'firstName')}
                        />
                        <Form.Control
                            type="text"
                            name="firstName"
                            placeholder="First Name"
                            onChange={handleChange}
                            isInvalid={!!errors.firstName}
                        />
                        <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="lastName">
                        <LabelField
                            label={questionPhrasing.lastName || t('personal.lastName')}
                            isRequired={isRequiredField(schema, 'lastName')}
                        />
                        <Form.Control
                            type="text"
                            name="lastName"
                            placeholder="Last Name"
                            onChange={handleChange}
                            isInvalid={!!errors.lastName}
                        />
                        <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                    </Form.Group>

                    {enabledFields.includes('dateOfBirth') && (
                        <Form.Group controlId="dob">
                            <LabelField
                                label={questionPhrasing.dob || t('personal.dob')}
                                isRequired={isRequiredField(schema, 'dob')}
                            />
                            <DatePickerField
                                className={!!errors.dob ? 'form-control is-invalid' : 'form-control'}
                                id="dob"
                                name="dob"
                                popperClassName="d-none" // remove this to show datepicker
                                placeholderText={dataTypes.dateFormat.toUpperCase()}
                                dateFormat={dataTypes.dateFormat}
                                maxDate={new Date()}
                                showYearDropdown
                                dateFormatCalendar="MMMM"
                                yearDropdownItemNumber={15}
                                scrollableYearDropdown
                                autoComplete="off"
                            />
                            <Form.Control.Feedback type="invalid">{errors.dob}</Form.Control.Feedback>
                        </Form.Group>
                    )}

                    {enabledFields.includes('age') && (
                        <Form.Group controlId="age">
                            <LabelField
                                label={questionPhrasing.age || t('personal.age')}
                                isRequired={isRequiredField(schema, 'age')}
                            />
                            <Form.Control
                                type="text"
                                id="age"
                                name="age"
                                onChange={handleChange}
                                className={!!errors.age ? 'form-control is-invalid' : 'form-control'}
                                placeholder={t('personal.age')}
                                autoComplete="off"
                            />
                            <Form.Control.Feedback type="invalid">{errors.age}</Form.Control.Feedback>
                        </Form.Group>
                    )}

                    <Form.Group controlId="email">
                        <LabelField label={questionPhrasing.email || t('personal.email')} isRequired={true} />
                        <Form.Control
                            type="text"
                            name="email"
                            value={values.email || ''}
                            placeholder="your.email@example.com"
                            onChange={handleEmailChange}
                            isInvalid={!!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                    </Form.Group>

                    {values.email && (
                    <Form.Group controlId="confirmEmail">
                        <LabelField label={t('personal.confirmEmail')} isRequired={true} />
                        <Form.Control
                            type="text"
                            name="confirmEmail"
                            value={values.confirmEmail || ''}
                            placeholder="your.email@example.com"
                            onChange={handleChange}
                            isInvalid={!!errors.confirmEmail}
                        />
                        <Form.Control.Feedback type="invalid">{errors.confirmEmail}</Form.Control.Feedback>
                    </Form.Group>
                    )}

                    <Form.Group controlId="phone">
                        <LabelField label={questionPhrasing.phone || t('personal.phone')} isRequired={true} />
                        <MaskedInput
                            className={errors.phone && touched.phone ? 'form-control is-invalid' : 'form-control'}
                            type="text"
                            name="phone"
                            value={values.phone || ''}
                            onChange={handlePhoneChange}
                            showMask={true}
                            mask={[
                                '+',
                                '1',
                                ' ',
                                '(',
                                /[1-9]/,
                                /\d/,
                                /\d/,
                                ')',
                                ' ',
                                /\d/,
                                /\d/,
                                /\d/,
                                '-',
                                /\d/,
                                /\d/,
                                /\d/,
                                /\d/,
                            ]}
                        />
                        <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                    </Form.Group>

                    {values.phone && values.phone.replace('+1', '').replace(/\D+/g, '').length > 0 && (
                    <Form.Group controlId="confirmPhone">
                        <LabelField label={t('personal.confirmPhone')} isRequired={true} />
                        <MaskedInput
                            className={errors.confirmPhone ? 'form-control is-invalid' : 'form-control'}
                            type="text"
                            name="confirmPhone"
                            value={values.confirmPhone || ''}
                            onChange={handleChange}
                            showMask={true}
                            mask={[
                                '+',
                                '1',
                                ' ',
                                '(',
                                /[1-9]/,
                                /\d/,
                                /\d/,
                                ')',
                                ' ',
                                /\d/,
                                /\d/,
                                /\d/,
                                '-',
                                /\d/,
                                /\d/,
                                /\d/,
                                /\d/,
                            ]}
                        />
                        {errors.confirmPhone && (
                            <div className="invalid-feedback d-block">{errors.confirmPhone}</div>
                        )}
                    </Form.Group>
                    )}

                    <p className="text-secondary" style={{ fontSize: '80%' }}>
                        {t('personal.emailAndOrPhone')}
                    </p>

                    {(values.email || (values.phone && values.phone.replace('+1', '').replace(/\D+/g, '').length > 0)) && (
                        <p className="alert alert-primary">
                            {t('account.create.yourLogin') + ' '}
                            <b>{values.email || getPhoneNumberWithCode(values.phone)}</b>
                        </p>
                    )}
                    {response !== null && <p className="text-danger">{response}</p>}
                    <Form.Group className="d-flex">
                        <Button as={Link} className="w-50 mr-2" variant="outline-secondary" to="/">
                            {t('core.action.back')}
                        </Button>
                        <Button className="w-50" variant="primary" type="submit" disabled={isLoading}>
                            {t(`core.action.${isLoading ? 'loading' : 'next'}`)}
                        </Button>
                    </Form.Group>
                    <ScrollToFirstError />
                </Form>
                );
            }}
        </Formik>
    );
};

export default CreateAccountFormStep1;
